import React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Wand2, Sparkles, Globe, ChevronLeft, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, serverTimestamp, collection } from 'firebase/firestore';
import { FestivalCreationHeader } from '../components/FestivalCreationHeader';
import toast from 'react-hot-toast';
import { MUSIC_STYLES, ELECTRO_SUBSTYLES } from '../utils/musicStyles';

const Cb = () => (
  <span className="inline-flex items-center text-[9px] font-black uppercase tracking-widest text-[#ff5e00] bg-[#ff5e00]/10 border border-[#ff5e00]/20 px-1.5 py-0.5 rounded-full ml-1.5 align-middle normal-case">
    Obligatoire
  </span>
);
const Rb = () => (
  <span className="inline-flex items-center text-[9px] font-black uppercase tracking-widest text-blue-400 bg-blue-400/10 border border-blue-400/20 px-1.5 py-0.5 rounded-full ml-1.5 align-middle normal-case">
    +Score
  </span>
);

export default function CreateFestivalPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  
  
  
  
  

  const [formData, setFormData] = useState({
    name: '',
    organizerName: '',
    category: '',
    customCategory: '',
    location: '',
    region: '',
    startDate: '',
    endDate: '',
    totalStands: '',
    dailyPax: '',
    terrainType: '',
    description: '',
    accessInfo: '',
    about: '',
    audienceProfile: '',
    editionNumber: '',
    pastAudience: '',
    historyDescription: '',
    musicStyles: [] as string[],
    musicSubstyles: [] as string[],
    ticketingUrl: '',
    allowedSlots: ['full_pass'] as string[],
    customTimeRange: '',
  });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const fetchUserAndFestival = async () => {
      // Fetch user organization name first for fallback
      let userOrg = '';
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            userOrg = userData.organizationName || `${userData.firstName} ${userData.lastName}`;
          }
        } catch (error) {
          console.error("Error fetching user info", error);
        }
      }

      // If we are forcing a new creation, clear session and don't fetch
      if (location.state?.forceNew) {
        sessionStorage.removeItem('activeFestivalId');
        setFormData(prev => ({ ...prev, organizerName: userOrg }));
        return;
      }

      // If a specific ID is passed in state, use it and store it
      if (location.state?.festivalId) {
        sessionStorage.setItem('activeFestivalId', location.state.festivalId);
      }

      const activeId = sessionStorage.getItem('activeFestivalId');
      if (user && activeId) {
        try {
          const docRef = doc(db, 'festivals', activeId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
              setFormData({
              name: data.name || '',
              organizerName: data.organizerName || userOrg, // Fallback if missing
              category: data.category || '',
              customCategory: data.customCategory || '',
              location: data.location || '',
              region: data.region || '',
              startDate: data.startDate || '',
              endDate: data.endDate || '',
              totalStands: data.totalStands ? data.totalStands.toString() : (data.capacity ? data.capacity.toString() : ''),
              dailyPax: data.dailyPax ? data.dailyPax.toString() : (data.pricing ? data.pricing.toString() : ''),
              terrainType: data.terrainType || '',
              description: data.description || '',
              accessInfo: data.accessInfo || '',
              about: data.about || '',
              audienceProfile: data.audienceProfile || '',
              editionNumber: data.editionNumber ? data.editionNumber.toString() : '',
              pastAudience: data.pastAudience || '',
              historyDescription: data.historyDescription || '',
              musicStyles: data.musicStyles || [],
              musicSubstyles: data.musicSubstyles || [],
              ticketingUrl: data.ticketingUrl || '',
              allowedSlots: data.allowedSlots || ['full_pass'],
              customTimeRange: data.customTimeRange || '',
            });
          }
        } catch (error) {
          console.error("Error fetching festival", error);
        }
      } else if (user) {
        // New festival case without activeId
        setFormData(prev => ({ ...prev, organizerName: userOrg }));
        
        // Handle prefilled data from Shotgun import
        if (location.state?.prefilledData) {
            const data = location.state.prefilledData;
            setFormData(prev => ({
                ...prev,
                name: data.nom || prev.name,
                location: data.localisation || prev.location,
                description: data.description || prev.description,
                accessInfo: data.acces || prev.accessInfo,
                about: data.description || prev.about,
                ticketingUrl: data.billetterie || prev.ticketingUrl,
                startDate: data.du || prev.startDate,
                endDate: data.au || prev.endDate,
                // Gemini renvoie directement un ID de catégorie ; filet de
                // sécurité : si la valeur n'est pas reconnue, on bascule sur "other".
                category: ['music','culture','convention','food','fair','market','sport','local','other'].includes(data.categorie)
                          ? data.categorie : 'other',
                customCategory: data.categorieLibre || prev.customCategory,
                // Filet de sécurité : ne garder que les IDs reconnus par le
                // formulaire, sinon les cases ne se cochent pas.
                musicStyles: Array.isArray(data.styles)
                    ? data.styles.filter((s: string) => MUSIC_STYLES.some(m => m.id === s))
                    : prev.musicStyles,
                musicSubstyles: Array.isArray(data.substyles)
                    ? data.substyles.filter((s: string) => ELECTRO_SUBSTYLES.some(m => m.id === s))
                    : prev.musicSubstyles,
            }));
            // We might need to handle dates if they fit the ISO format for date input
        }
      }
    };
    fetchUserAndFestival();
  }, [user, location.state]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleArrayItem = (fieldName: 'musicStyles' | 'allowedSlots' | 'musicSubstyles', item: string) => {
    setFormData(prev => {
      const current = prev[fieldName] as string[];
      const next = current.includes(item)
        ? current.filter(i => i !== item)
        : [...current, item];
      return { ...prev, [fieldName]: next };
    });
  };

  // MUSIC_STYLES / ELECTRO_SUBSTYLES : importés depuis utils/musicStyles
  // (source unique partagée avec le profil exposant et le matching).

  const TIME_SLOTS = [
    { id: 'day', label: 'Journée', description: 'Ouverture midi - 20h' },
    { id: 'night', label: 'Nuit', description: 'Ouverture 20h - matin' },
    { id: 'full_pass', label: 'Full Pass', description: 'Toute la durée' },
    { id: 'custom', label: 'Plage libre', description: 'Horaires sur mesure' }
  ];

  const getMinDate = () => {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 15);
    return minDate.toISOString().split('T')[0];
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    // Validate 15-day restriction
    const minDate = new Date(getMinDate());
    const startDate = new Date(formData.startDate);
    if (startDate < minDate) {
      toast.error("Le festival doit commencer au moins 15 jours après aujourd'hui.");
      return;
    }

    setIsSaving(true);
    try {
      let activeId = sessionStorage.getItem('activeFestivalId');
      
      // Generate a new ID if we don't have one
      if (!activeId) {
        const newDocRef = doc(collection(db, 'festivals'));
        activeId = newDocRef.id;
        sessionStorage.setItem('activeFestivalId', activeId);
      }

      const festivalRef = doc(db, 'festivals', activeId);
      await setDoc(festivalRef, {
        ...formData,
        id: activeId, // Explicitly store ID
        totalStands: formData.totalStands ? parseInt(formData.totalStands) : 0,
        capacity: formData.totalStands ? parseInt(formData.totalStands) : 0, // Keep both for backward compat
        dailyPax: formData.dailyPax ? parseInt(formData.dailyPax) : 0,
        ownerId: user.uid,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(), 
      }, { merge: true });
      
      navigate('/create-festival/step-2');
    } catch (error) {
      console.error("Error saving festival", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans relative overflow-x-hidden pb-24">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-gradient-to-b from-[#ff5a00]/10 to-transparent blur-3xl pointer-events-none" />
      
      {/* Header */}
      <FestivalCreationHeader onBack={() => navigate(-1)} />

      {/* Main Content */}
      <main className="relative z-30 max-w-4xl mx-auto px-4 md:px-6 pt-8 md:pt-12">
        
          <div className="max-w-3xl mx-auto">
            {/* Progress Bar */}
            <div className="mb-12">
              <div className="flex items-center justify-between text-xs font-black text-gray-500 mb-4 uppercase tracking-[0.2em]">
                <span>Infos principales • Étape 1/5</span>
                <span className="text-[#ff5e00]">Bases de l'édition</span>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {[1, 2, 3, 4, 5].map((step) => (
                  <div 
                    key={step}
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      step <= 1 
                        ? 'bg-[#FF5E00] shadow-[0_0_10px_rgba(255,94,0,0.4)]' 
                        : 'bg-white/5'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="bg-white/5 border border-white/10 rounded-[32px] md:rounded-[40px] p-6 md:p-12 backdrop-blur-2xl relative shadow-2xl overflow-hidden">
               <form onSubmit={handleSave} className="space-y-6 md:space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                     <div className="md:col-span-2">
                       <label className="block text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest">Nom du Festival <Cb /></label>
                       <input 
                         type="text" 
                         name="name"
                         value={formData.name}
                         onChange={handleInputChange}
                         placeholder="Ex: Main Square Festival"
                         className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all"
                         required
                       />
                    </div>
                    <div className="md:col-span-2">
                       <label className="block text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest">Organisé par</label>
                       <input 
                         type="text" 
                         name="organizerName"
                         value={formData.organizerName}
                         onChange={handleInputChange}
                         placeholder="Ex: Association Live Nation / Ville d'Arras"
                         className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all"
                         required
                       />
                    </div>
                    <div>
                       <label className="block text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest">Catégorie <Cb /></label>
                       <select 
                         name="category"
                         value={formData.category}
                         onChange={handleInputChange}
                         className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all appearance-none cursor-pointer"
                         required
                       >
                         <option value="">Sélectionner...</option>
                         <option value="music">Festival de Musique</option>
                         <option value="culture">Festival Culturel & Artistique</option>
                         <option value="convention">Convention & Pop-Culture</option>
                         <option value="food">Événement Gastronomique & Terroir</option>
                         <option value="fair">Foire, Salon & Exposition</option>
                         <option value="market">Marché Éphémère & Pop-up</option>
                         <option value="sport">Événement Sportif & Outdoor</option>
                         <option value="local">Fête Locale & Rassemblement Citoyen</option>
                         <option value="other">Autre (à préciser)</option>
                       </select>
                    </div>

                    {formData.category === 'other' && (
                      <div className="md:col-span-2">
                        <label className="block text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest">Précisez la catégorie</label>
                        <input 
                          type="text" 
                          name="customCategory"
                          value={formData.customCategory}
                          onChange={handleInputChange}
                          autoFocus
                          placeholder="Ex: Festival de Cirque, Cinéma en plein air..."
                          className="w-full bg-black/40 border border-[#ff5e00]/30 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all"
                          required
                        />
                      </div>
                    )}

                    <div>
                       <label className="block text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest">Localisation <Cb /></label>
                       <input
                         type="text"
                         name="location"
                         placeholder="Ex : Parc des Expositions, Lyon"
                         value={formData.location}
                         onChange={handleInputChange}
                         className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all"
                         required
                       />
                    </div>

                    <div>
                       <label className="block text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest">Région <Cb /></label>
                       <select
                         name="region"
                         value={formData.region}
                         onChange={handleInputChange}
                         className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all text-white appearance-none"
                         required
                       >
                         <option value="">Sélectionner une région</option>
                         <option>Île-de-France</option>
                         <option>Auvergne-Rhône-Alpes</option>
                         <option>Provence-Alpes-Côte d'Azur</option>
                         <option>Occitanie</option>
                         <option>Nouvelle-Aquitaine</option>
                         <option>Bretagne</option>
                         <option>Pays de la Loire</option>
                         <option>Normandie</option>
                         <option>Grand Est</option>
                         <option>Hauts-de-France</option>
                         <option>Centre-Val de Loire</option>
                         <option>Bourgogne-Franche-Comté</option>
                         <option>Corse</option>
                       </select>
                    </div>

                    <div className="md:col-span-2 grid grid-cols-2 gap-3 md:gap-8 min-w-0">
                      <div className="min-w-0">
                         <label className="block text-[10px] md:text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest truncate">Date Début</label>
                         <input 
                           type="date" 
                           name="startDate"
                           value={formData.startDate}
                           min={getMinDate()}
                           onChange={handleInputChange}
                           className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-2 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all color-scheme-dark text-[11px] sm:text-sm md:text-base min-w-0"
                           required
                         />
                      </div>
  
                      <div className="min-w-0">
                         <label className="block text-[10px] md:text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest truncate">Date Fin</label>
                         <input 
                           type="date" 
                           name="endDate"
                           value={formData.endDate}
                           min={formData.startDate || getMinDate()}
                           onChange={handleInputChange}
                           className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-2 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all color-scheme-dark text-[11px] sm:text-sm md:text-base min-w-0"
                           required
                         />
                      </div>
                    </div>

                    <div>
                       <label className="block text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest">Affluence quotidienne estimée</label>
                       <input 
                         type="number" 
                         name="dailyPax"
                         value={formData.dailyPax}
                         onChange={handleInputChange}
                         className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all"
                         required
                       />
                    </div>

                    <div className="md:col-span-2">
                       <label className="block text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest">À propos du festival</label>
                       <textarea 
                         name="about"
                         value={formData.about}
                         onChange={handleInputChange}
                         rows={3}
                         className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all resize-none"
                         placeholder="L'histoire, les valeurs, le concept..."
                       />
                    </div>

                    {formData.category === 'music' && (
                      <div className="md:col-span-2">
                        <label className="block text-xs font-black text-gray-400 mb-4 uppercase tracking-widest">Styles Musicaux</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
                          {MUSIC_STYLES.map(style => (
                            <button
                              key={style.id}
                              type="button"
                              onClick={() => toggleArrayItem('musicStyles', style.id)}
                              className={`py-2 md:py-3 px-3 md:px-4 rounded-lg md:rounded-xl text-[10px] md:text-xs font-bold border transition-all ${
                                formData.musicStyles.includes(style.id)
                                  ? 'bg-[#ff5e00] border-[#ff5e00] text-white'
                                  : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                              }`}
                            >
                              {style.label}
                            </button>
                          ))}
                        </div>
                        {formData.musicStyles.includes('electro') && (
                          <div className="mt-4 md:mt-6 p-4 md:p-6 bg-white/5 border border-white/10 rounded-xl md:rounded-2xl">
                             <label className="block text-[10px] md:text-xs font-black text-gray-400 mb-4 uppercase tracking-widest">Sous-genres Électronique</label>
                             <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 md:gap-3">
                               {ELECTRO_SUBSTYLES.map(substyle => (
                                 <button
                                   key={substyle.id}
                                   type="button"
                                   onClick={() => toggleArrayItem('musicSubstyles', substyle.id)}
                                   className={`py-2 px-2 md:px-3 rounded-lg text-[9px] md:text-[10px] font-bold border transition-all ${
                                     formData.musicSubstyles.includes(substyle.id)
                                       ? 'bg-[#ff5e00]/80 border-[#ff5e00] text-white'
                                       : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                                   }`}
                                 >
                                   {substyle.label}
                                 </button>
                               ))}
                             </div>
                          </div>
                        )}
                      </div>
                    )}

                      <div className="md:col-span-2">
                        <label className="block text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest">Billetterie (Lien)</label>
                        <input 
                          type="url" 
                          name="ticketingUrl"
                          value={formData.ticketingUrl}
                          onChange={handleInputChange}
                          className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all text-sm"
                          placeholder="https://shotgun.live/fr/events/..."
                        />
                      </div>

                    <div className="md:col-span-2">
                       <label className="block text-xs font-black text-gray-400 mb-3 uppercase tracking-widest">Comment y accéder ?</label>
                       <textarea 
                         name="accessInfo"
                         value={formData.accessInfo}
                         onChange={handleInputChange}
                         rows={2}
                         className="w-full bg-black/40 border border-white/10 rounded-2xl px-6 py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all resize-none"
                         placeholder="Trains, bus, navettes, parkings..."
                       />
                    </div>

                    <div className="md:col-span-2">
                       <label className="block text-xs font-black text-gray-400 mb-4 uppercase tracking-widest">Créneaux horaires autorisés pour les stands</label>
                       <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                         {TIME_SLOTS.map(slot => (
                           <button
                             key={slot.id}
                             type="button"
                             onClick={() => toggleArrayItem('allowedSlots', slot.id)}
                             className={`p-4 rounded-2xl text-left border transition-all ${
                               formData.allowedSlots.includes(slot.id)
                                 ? 'bg-[#ff5e00]/10 border-[#ff5e00] text-white shadow-[0_0_20px_rgba(255,138,0,0.1)]'
                                 : 'bg-white/5 border-white/10 text-gray-500 hover:border-white/20'
                             }`}
                           >
                             <div className="font-black text-sm mb-1">{slot.label}</div>
                             <div className="text-[10px] opacity-60 leading-tight">{slot.description}</div>
                           </button>
                         ))}
                       </div>

                       {formData.allowedSlots.includes('custom') && (
                         <div className="mt-4 animate-in fade-in slide-in-from-top-2 duration-300">
                           <label className="block text-[10px] font-black text-[#ff5e00] mb-2 uppercase tracking-widest">Précisez vos horaires (ex: 14h - 02h)</label>
                           <input 
                             type="text" 
                             name="customTimeRange"
                             value={formData.customTimeRange}
                             onChange={handleInputChange}
                             placeholder="Ex: 14h00 - 02h00"
                             className="w-full bg-black/40 border border-[#ff5e00]/30 rounded-xl px-6 py-3 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all"
                           />
                         </div>
                       )}
                    </div>

                    <div className="md:col-span-2">
                       <label className="block text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest flex items-center">
                         Profil du Public (Vibe Check) 
                         <span className="ml-2 px-2 py-0.5 bg-gray-500/20 text-gray-400 rounded text-[8px] tracking-normal">Optionnel</span>
                       </label>
                       <textarea 
                         name="audienceProfile"
                         value={formData.audienceProfile}
                         onChange={handleInputChange}
                         rows={2}
                         className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all resize-none text-sm"
                         placeholder="Ex: 18-35 ans, technophiles..."
                       />
                       <p className="mt-2 text-[9px] md:text-[10px] text-gray-500 italic">Aide les exposants à adapter leur stock.</p>
                    </div>

                    <div className="md:col-span-2">
                       <label className="block text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest">Texte d'appel (Optionnel)</label>
                       <textarea 
                         name="description"
                         value={formData.description}
                         onChange={handleInputChange}
                         rows={4}
                         className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all resize-none text-sm"
                         placeholder="Présentez votre événement..."
                       />
                    </div>

                    <div className="md:col-span-2 py-2 md:py-4">
                        <div className="border-t border-white/5 pt-6 md:pt-8 mb-4 md:mb-6">
                          <h3 className="text-xs md:text-sm font-black uppercase tracking-[0.2em] text-[#ff5e00]">Historique & Notoriété</h3>
                          <p className="text-[9px] md:text-[10px] text-gray-500 font-bold uppercase mt-1">Donnez de la visibilité sur vos éditions précédentes</p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                          <div>
                            <label className="block text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest text-[#ff5e00]">Numéro d'édition</label>
                            <input 
                              type="number" 
                              name="editionNumber"
                              value={formData.editionNumber}
                              onChange={handleInputChange}
                              placeholder="Ex: 5"
                              className="w-full bg-black/40 border border-[#ff5e00]/20 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all"
                            />
                            <p className="mt-2 text-[9px] md:text-[10px] text-gray-500 italic">S'il s'agit d'une première ou reconduction.</p>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest">Affluence Passée</label>
                            <input 
                              type="text" 
                              name="pastAudience"
                              value={formData.pastAudience}
                              onChange={handleInputChange}
                              placeholder="Ex: 12 000"
                              className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all"
                            />
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-xs font-black text-gray-400 mb-2 md:mb-3 uppercase tracking-widest">Historique & Faits Marquants</label>
                            <textarea 
                              name="historyDescription"
                              value={formData.historyDescription}
                              onChange={handleInputChange}
                              rows={3}
                              className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 focus:outline-none focus:ring-2 focus:ring-[#ff5e00] transition-all resize-none text-sm"
                              placeholder="Évolution du projet..."
                            />
                          </div>
                        </div>
                     </div>
                  </div>

                  <div className="pt-6 md:pt-8 border-t border-white/5 flex flex-col md:flex-row gap-4 items-center justify-between">
                     <p className="text-[10px] md:text-xs text-gray-500 font-medium text-center md:text-left">L'identité visuelle et le branding seront configurés ensuite.</p>
                     <button 
                       type="submit"
                       disabled={isSaving}
                       className="w-full md:w-auto bg-white text-black font-black py-4 px-10 md:px-12 rounded-xl md:rounded-2xl hover:bg-[#ff5e00] hover:text-white transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                     >
                       {isSaving ? 'ENREGISTREMENT...' : 'CONTINUER'}
                       <ArrowRight className="w-5 h-5" />
                     </button>
                  </div>
               </form>
            </div>
          </div>
      </main>

      <style>{`
        .color-scheme-dark { color-scheme: dark; }
      `}</style>
    </div>
  );
}

